import { useEffect } from "react";
import { Divider, MenuItem, Select as MuiSelect } from "@mui/material";
import type {
  ProcessedFormField,
  ResolvedOption,
} from "@/types/api/formSchema";
import { resolveSelectOptions } from "../utils/schemaUtils";

type Props = {
  field: ProcessedFormField & { resolvedOptions?: ResolvedOption[] };
  value: string;
  onChange: (value: string) => void;
  onBlur: () => void;
  name: string;
};

export function SelectField({
  field: schemaField,
  value,
  onChange,
  onBlur,
  name,
}: Props) {
  const options = schemaField.resolvedOptions ?? resolveSelectOptions(schemaField);
  const showClear = !schemaField.required && !!value;
  const stale =
    !!value &&
    schemaField.resolvedOptions !== undefined &&
    !schemaField.resolvedOptions.some((o) => o.name === value);

  // A disabled field is not the user's to lose a value from.
  useEffect(() => {
    if (stale && !schemaField.disabled) onChange("");
  }, [stale, schemaField.disabled, onChange]);

  return (
    <MuiSelect
      id={name}
      name={name}
      value={value ?? ""}
      displayEmpty
      disabled={!!schemaField.disabled}
      onChange={(e) => onChange(e.target.value as string)}
      onBlur={onBlur}
      fullWidth
      size="small"
      variant="outlined"
      renderValue={(v) => {
        if (!v) {
          return (
            <span style={{ color: "rgba(0,0,0,0.4)" }}>
              {schemaField.placeholder ?? "Select..."}
            </span>
          );
        }
        const opt = options.find((o) => o.name === v);
        return opt?.display_name ?? String(v);
      }}
    >
      {showClear && [
        <MenuItem
          key="__clear__"
          value=""
          sx={{ color: "text.secondary", fontStyle: "italic" }}
        >
          None (clear selection)
        </MenuItem>,
        <Divider key="__clear-divider__" />,
      ]}
      {options.map((opt) => (
        <MenuItem
          key={opt.name}
          value={opt.name}
          disabled={"disabled" in opt && !!(opt as ResolvedOption).disabled}
        >
          {opt.display_name}
        </MenuItem>
      ))}
    </MuiSelect>
  );
}
